import { showConfirmDialog } from 'vant';
import i18n from '@/i18n';
import { detectAppType, isIosSafariBrowser } from '@/utils/device';

function isLocalDebugHost(hostname = '') {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '[::1]';
}

export function isCameraSecureContext() {
  const { protocol, hostname } = window.location;
  if (protocol === 'https:') {
    return true;
  }
  return isLocalDebugHost(hostname);
}

export function isCameraPermissionDeniedError(error) {
  const name = String(error?.name || '');
  return name === 'NotAllowedError' || name === 'PermissionDeniedError';
}

export function mapCameraErrorMessage(error) {
  const t = i18n.global.t;
  const name = String(error?.name || '');
  if (isCameraPermissionDeniedError(error)) {
    return t('wallet.cameraPermissionDenied');
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return t('wallet.cameraNotFound');
  }
  if (name === 'NotReadableError' || name === 'TrackStartError') {
    return t('wallet.cameraInUse');
  }
  if (name === 'OverconstrainedError' || name === 'ConstraintNotSatisfiedError') {
    return t('wallet.cameraConstraintFailed');
  }
  if (name === 'SecurityError') {
    return t('wallet.cameraSecureContextRequired');
  }
  return t('wallet.cameraUnavailable');
}

export function resolveCameraGuideType(error, fallbackType = 'generic') {
  const name = String(error?.name || '');
  if (isCameraPermissionDeniedError(error)) {
    return 'permission';
  }
  if (name === 'SecurityError') {
    return 'secure';
  }
  return fallbackType;
}

function resolveCameraGuideText(type = 'generic') {
  const t = i18n.global.t;
  const platform = detectAppType();
  const isIos = platform === 'ios';
  const isAndroid = platform === 'android';
  const isIosSafari = isIosSafariBrowser();

  const title = type === 'scanUnsupported'
    ? (isIosSafari
        ? t('wallet.safariScanGuideTitle')
        : isAndroid
          ? t('wallet.androidScanGuideTitle')
          : isIos
            ? t('wallet.iosCameraGuideTitle')
            : t('wallet.cameraGuideTitle'))
    : (isIos
        ? t('wallet.iosCameraGuideTitle')
        : isAndroid
          ? t('wallet.androidCameraGuideTitle')
          : t('wallet.cameraGuideTitle'));

  const messageKeyMap = isIos
    ? {
        permission: 'wallet.iosCameraPermissionGuide',
        secure: 'wallet.iosCameraSecureGuide',
        scanUnsupported: isIosSafari ? 'wallet.safariScanGuide' : 'wallet.scanNotSupportedGuide',
        unavailable: 'wallet.iosCameraUnavailableGuide',
        generic: 'wallet.iosCameraUnavailableGuide'
      }
    : isAndroid
      ? {
          permission: 'wallet.androidCameraPermissionGuide',
          secure: 'wallet.androidCameraSecureGuide',
          scanUnsupported: 'wallet.androidScanGuide',
          unavailable: 'wallet.androidCameraUnavailableGuide',
          generic: 'wallet.androidCameraUnavailableGuide'
        }
      : {
          permission: 'wallet.cameraPermissionGuide',
          secure: 'wallet.cameraSecureGuide',
          scanUnsupported: 'wallet.scanNotSupportedGuide',
          unavailable: 'wallet.cameraUnavailableGuide',
          generic: 'wallet.cameraUnavailableGuide'
        };

  return {
    title,
    message: t(messageKeyMap[type] || messageKeyMap.generic)
  };
}

export function showCameraGuide(type = 'generic') {
  const t = i18n.global.t;
  const { title, message } = resolveCameraGuideText(type);
  return showConfirmDialog({
    title,
    message,
    allowHtml: true,
    messageAlign: 'left',
    showCancelButton: false,
    confirmButtonText: t('common.confirm')
  }).catch(() => {});
}
